import { BaseResponse } from '../interfaces/BaseResponse.interface'
import i18n from "../i18n.ts"
import { useLoadingStore } from '../store/Loading'
import { getTurnstileToken, needsTurnstile, TURNSTILE_HEADER } from './Turnstile'

// skipGlobalLoading: a tela mostra o seu próprio loading (ex.: overlay do Prompt, spinner de lista) e o overlay global não aparece por cima.
export interface FetchConfig {
    skipGlobalLoading?: boolean;
    isRetry?: boolean;
}

interface RequestOptions {
    method: string;
    headers: Record<string, string>;
    body?: string;
}

export const fetchWrapper = {
    get: request('GET'),
    post: request('POST'),
    put: request('PUT'),
    delete: request('DELETE')
};

function request(method: string) {
    return async (url: string, body?: any, config: FetchConfig = {}): Promise<BaseResponse<any>> => {
        const loadingStore = useLoadingStore();
        const isRetry = config.isRetry ?? false;
        // O retry roda dentro da requisição original, que já segura o contador global
        const useGlobalLoading = !isRetry && !config.skipGlobalLoading;

        // Incrementar contador de requisições (apenas se não for retry)
        if (useGlobalLoading) {
            loadingStore.increment();
        }
        
        const requestOptions: RequestOptions = {
            method,
            headers: authHeader()
        };
        if (body) {
            requestOptions.headers['Content-Type'] = 'application/json';
            requestOptions.body = JSON.stringify(body);
        }

        requestOptions.headers['SelectedLanguage'] = (i18n.global.locale == "pt") ? "pt-BR" : "en-US";

        // Bot challenge for login, sign-up, recovery and contact. No-op unless VITE_TURNSTILE_SITE_KEY is set.
        if (needsTurnstile(url)) {
            try {
                const turnstileToken = await getTurnstileToken();
                if (turnstileToken) {
                    requestOptions.headers[TURNSTILE_HEADER] = turnstileToken;
                }
            } catch (error) {
                // Never leave the global loading counter incremented; the API decides what to do without a token.
                console.log(error);
            }
        }

        return fetch(url, requestOptions)
            .then(response => handleResponse(response, url, body, method, config))
            .finally(() => {
                // Decrementar contador de requisições (apenas se não for retry)
                if (useGlobalLoading) {
                    loadingStore.decrement();
                }
            });
    }
}


function authHeader(): Record<string, string> {
    let token = localStorage.getItem('token');
    if (token) {
        return { Authorization: `Bearer ${token}` };
    } else {
        return {};
    }
}

async function handleResponse(response: Response, url: string, body: any, method: string, config: FetchConfig): Promise<BaseResponse<any>> {
    const isRetry = config.isRetry ?? false;
    const isJson: boolean = response.headers?.get('content-type')?.includes('application/json') ?? false;
    const data: any = isJson ? await response.json() : null;
    let baseResponse: BaseResponse<any> = { success: false };

    //console.log('data', data);
    if (Array.isArray(data)) {
        baseResponse.items = data;
    }
    else {
        baseResponse.value = data;

        if (data && data.link) {
            baseResponse.link = data.link;
        }

        if (data && data.count) {
            baseResponse.count = data.count;
        }

        if (data && data.items) {
            baseResponse.items = data.items;
        }

        if (data && data.message) {
            baseResponse.message = data.message;
        }

        if (data && data.title) {
            baseResponse.message = data.title;
        }

        if (data && data.detail) {
            baseResponse.message = data.detail;
        }

        if (data && data.errors) {
            baseResponse.errors = data.errors;
        }
    }
    //console.log('response', response);
    if (!response.ok) {
        if (response.status === 401 && !isRetry) {
            const refreshSuccess = await refreshToken();
            
            if (refreshSuccess) {
                // Retentar a requisição original
                console.log('Retrying request after token refresh');
                return request(method)(url, body, { ...config, isRetry: true });
            } else {
                console.log('Token refresh failed');
                baseResponse.message = 'Session expired. Please login again.';
                baseResponse.success = false;
                return baseResponse;
            }
        }

        if(baseResponse.message == null){
            baseResponse.message =  response.status.toString();
        }
        
        baseResponse.success = false;

    }else{
        baseResponse.success = true;
    }

    return baseResponse;
}


async function refreshToken(): Promise<boolean> {
        try {
            console.log('refreshToken called');
            const baseUrl: string = `${import.meta.env.VITE_API_URL}/security`;
            let token = localStorage.getItem('token');
            let refreshTokenValue = localStorage.getItem('refreshToken');
            
            const response = await fetch(`${baseUrl}/RefreshToken`, {
                method: 'POST',
                headers: {
                    ...authHeader(),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ token: token, refreshToken: refreshTokenValue })
            });
            
            const data =  await response.json();

            console.log('refreshToken response data:', data);
            
            if (response.ok && data && (data.token || data.value?.token)) {
                console.log('refreshToken successful');
                const tokenData = data.token ? data : data.value;
                if (tokenData.token) {
                    localStorage.setItem('token', tokenData.token);
                }
                if (tokenData.refreshToken) {
                    localStorage.setItem('refreshToken', tokenData.refreshToken);
                }
                return true;
            } else {
                console.log('refreshToken failed');
                return false;
            }
        } catch (error) {
            console.error('refreshToken error:', error);
            return false;
        }
}
